import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getProjectSchedule, createProjectSchedule, updateProjectSchedule, updateScheduleStep, freezeSchedule,
  setTaskResponsible, decideRevision, downloadRevisionDocument,
} from '../services/scheduleService';
import { downloadBlob } from '../utils/downloadFile';
import ScheduleTaskForm from './ScheduleTaskForm';
import { getDepartments, getEmployees } from '../services/employeeService';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import {
  SCHEDULE_STATUS_COLORS, SCHEDULE_STATUS_LABELS, SCHEDULE_BAR_COLORS, daysBetween, formatSlip,
  HEALTH_COLORS, HEALTH_LABELS,
} from '../constants/schedule';

// Project schedule (Gantt). Three states, driven by the backend:
//   no schedule -> PM creates it from the company template with the PO date
//   draft       -> PM adjusts durations / start dates / departments, then freezes Rev 0
//   baselined   -> read-only plan vs forecast; departments update progress from
//                  /schedule/my-tasks, which this panel links to
const ProjectSchedulePanel = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ start_date: '', contractual_handover_date: '' });
  const [formError, setFormError] = useState('');
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    getProjectSchedule(projectId)
      .then((res) => setData(res.data))
      .catch(() => showToast('Failed to load schedule', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data?.can_plan && data?.schedule?.status === 'draft') {
      getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});
    }
  }, [data?.can_plan, data?.schedule?.status]);

  // Employees feed the PM picker (draft) and the per-line Responsible picker
  // (draft and after Rev 0 - it changes who is reminded, not the plan).
  useEffect(() => {
    if (data?.can_plan && data?.schedule) {
      getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    }
  }, [data?.can_plan, !!data?.schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (promise, successMessage) =>
    promise
      .then((res) => {
        // Only the GET carries the review-based health; keep it across edits.
        setData((prev) => (res.data.schedule
          ? { ...res.data, schedule: { health: prev?.schedule?.health, ...res.data.schedule } }
          : res.data));
        if (successMessage) showToast(successMessage, 'success');
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to save', 'error'));

  const handleCreate = () => {
    if (!form.start_date) {
      setFormError('Enter the PO date');
      return;
    }
    apply(
      createProjectSchedule(projectId, {
        start_date: form.start_date,
        contractual_handover_date: form.contractual_handover_date || null,
      }),
      'Schedule created from template',
    );
  };

  const handleResponsible = (task, value) =>
    apply(setTaskResponsible(projectId, task.id, value ? Number(value) : null), 'Responsible person updated');

  const handleFreeze = () => {
    if (!window.confirm('Freeze this plan as Rev 0? After this, dates change only through a revision.')) return;
    apply(freezeSchedule(projectId), 'Rev 0 frozen');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading schedule...</p>;

  if (!data?.schedule) {
    if (!data?.can_plan) return <p className="text-sm text-gray-400">No schedule created for this project yet.</p>;
    return (
      <div>
        <p className="text-xs text-gray-500 mb-3">
          Creates the standard project steps from the company template. You can adjust durations, start dates and
          departments before freezing the plan as Rev 0.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <span className="block text-xs text-gray-500 mb-1">PO date (step 1)</span>
            <input type="date" className="border border-gray-300 rounded px-3 py-2 text-sm" value={form.start_date}
              onChange={(e) => { setForm({ ...form, start_date: e.target.value }); setFormError(''); }} />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500 mb-1">Contractual handover (from PO)</span>
            <input type="date" className="border border-gray-300 rounded px-3 py-2 text-sm" value={form.contractual_handover_date}
              onChange={(e) => setForm({ ...form, contractual_handover_date: e.target.value })} />
          </label>
          <button onClick={handleCreate} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Create schedule</button>
        </div>
        {formError && <p className="text-sm text-red-600 mt-2">{formError}</p>}
      </div>
    );
  }

  const { schedule, steps, baselines, can_plan: canPlan } = data;
  const isDraft = schedule.status === 'draft';

  return (
    <div>
      {data.pending_revision && (
        <PendingRevisionCard revision={data.pending_revision} projectId={projectId} onDecided={load} />
      )}

      <ScheduleSummary schedule={schedule} cost={data.cost} employees={employees} canPlan={canPlan} canEditPm={canPlan && isDraft}
        onSave={(patch, msg) => apply(updateProjectSchedule(projectId, patch), msg)} />

      <GanttChart schedule={schedule} steps={steps} />

      {isDraft ? (
        <DraftTable steps={steps} departments={departments} canPlan={canPlan} employees={employees}
          onSave={(stepId, patch) => apply(updateScheduleStep(projectId, stepId, patch))}
          onResponsible={handleResponsible} />
      ) : (
        <TrackingTable steps={steps} canPlan={canPlan} employees={employees} onResponsible={handleResponsible} onTaskSaved={load} />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        {isDraft ? (
          <p className="text-xs text-gray-500">Draft plan. Departments can start entering progress once it's frozen as Rev 0.</p>
        ) : (
          <div className="flex gap-4 flex-wrap">
            <Link to="/schedule/my-tasks" className="text-sm text-komal-blue hover:underline">Update department progress &rarr;</Link>
            {schedule.weekly_monitoring && (
              <Link to={`/schedule/reviews/${projectId}`} className="text-sm text-komal-blue hover:underline">Weekly review &rarr;</Link>
            )}
          </div>
        )}
        {isDraft && canPlan && <button onClick={handleFreeze} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Freeze as Rev 0</button>}
        {!isDraft && canPlan && !data.pending_revision && (
          <Link to={`/projects/${projectId}/revision`} className="px-4 py-2 text-sm rounded border border-komal-blue text-komal-blue hover:bg-komal-blue/5 font-semibold">
            Raise revision
          </Link>
        )}
      </div>

      {baselines.length > 0 && <RevisionLog baselines={baselines} projectId={projectId} />}
    </div>
  );
};

const ScheduleSummary = ({ schedule, cost, employees, canPlan, canEditPm, onSave }) => {
  const slip = schedule.handover_slip_days;
  const tile = (label, value, tone = '') => (
    <div className="bg-gray-50 rounded p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`font-semibold ${tone}`}>{value}</div>
    </div>
  );
  return (
    <div className="mb-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        {tile('Baseline', schedule.current_rev === null ? 'Draft' : `Rev ${schedule.current_rev}`)}
        {tile('Contractual handover', formatDateDDMMYY(schedule.contractual_handover_date) || '—')}
        {tile(schedule.current_rev === null ? 'Planned handover' : 'Forecast handover',
          formatDateDDMMYY(schedule.forecast_handover) || '—', slip > 0 ? 'text-red-700' : '')}
        {tile('Slip vs baseline', schedule.current_rev === null ? '—' : formatSlip(slip), slip > 0 ? 'text-red-700' : 'text-green-700')}
        {tile('Progress actual / plan', schedule.current_rev === null ? '—' : `${schedule.progress_actual}% / ${schedule.progress_planned}%`)}
      </div>
      {cost && <CostStrip cost={cost} progress={schedule.progress_actual} baselined={schedule.current_rev !== null} />}
      <div className="text-xs text-gray-500 mt-3 flex items-center gap-2 flex-wrap">
        <span>PM:</span>
        {canEditPm ? (
          <select className="border border-gray-300 rounded px-2 py-1 text-xs" value={schedule.pm_id || ''}
            onChange={(e) => onSave({ pm_id: Number(e.target.value) }, 'PM updated')}>
            <option value="">Select PM</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        ) : (
          <span className="font-medium text-gray-700">{schedule.pm_name || '—'}</span>
        )}
        <span>· PO date {formatDateDDMMYY(schedule.start_date)}</span>
        <label className={`flex items-center gap-1 ml-3 ${canPlan ? 'cursor-pointer' : ''}`}
          title="Only monitored projects appear in department schedule updates and the weekly report">
          <input type="checkbox" checked={schedule.weekly_monitoring} disabled={!canPlan}
            onChange={(e) => onSave({ weekly_monitoring: e.target.checked },
              e.target.checked ? 'Weekly monitoring on' : 'Weekly monitoring off')} />
          Weekly monitoring
        </label>
        {schedule.weekly_monitoring && (
          <label className={`flex items-center gap-1 ${canPlan ? 'cursor-pointer' : ''}`}
            title="Key projects need the PM's weekly review and always get a full section in the Monday email">
            <input type="checkbox" checked={!!schedule.is_key} disabled={!canPlan}
              onChange={(e) => onSave({ is_key: e.target.checked }, e.target.checked ? 'Marked as Key project' : 'No longer a Key project')} />
            Key project <span className="text-gray-400">(weekly PM review required)</span>
          </label>
        )}
        {schedule.weekly_monitoring && schedule.current_rev !== null && (
          <>
            <span className="px-2 py-0.5 rounded-full bg-komal-blue/10 text-komal-blue font-semibold">Monitored</span>
            {schedule.is_key && <span className="px-2 py-0.5 rounded-full bg-komal-blue text-white font-semibold">Key</span>}
            <span className={`px-2 py-0.5 rounded font-semibold ${HEALTH_COLORS[schedule.health] || 'bg-gray-100 text-gray-600'}`}>
              {schedule.health ? HEALTH_LABELS[schedule.health] : 'No review yet'}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const lakh = (v) => `₹${(Number(v || 0) / 100000).toFixed(1)} L`;

// Budget vs actual - the same figures as the Expenses dashboard's
// budget-vs-actual panel (the backend reuses that calculation), with spend %
// set against physical progress % so overspend shows before the budget runs out.
const CostStrip = ({ cost, progress, baselined }) => {
  const bucket = (label, spent, budget) => {
    const pct = budget ? Math.min(100, Math.round((100 * spent) / budget)) : 0;
    return (
      <div>
        <div className="flex justify-between text-xs">
          <span>{label}</span>
          <span className={budget && spent > budget ? 'text-red-700 font-semibold' : 'text-gray-600'}>
            {lakh(spent)}{budget ? ` / ${lakh(budget)}` : ' (no budget)'}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded mt-1 overflow-hidden">
          <div className={`h-full ${budget && spent > budget ? 'bg-red-400' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };
  return (
    <div className="border border-gray-200 rounded p-3 mt-3">
      <div className="flex justify-between items-baseline flex-wrap gap-2">
        <span className="text-sm font-semibold text-gray-900">Cost vs budget</span>
        {cost.spend_pct !== null && baselined && (
          <span className="text-xs text-gray-500">
            Spent {cost.spend_pct}% of budget at {progress}% progress
            {cost.overspend_risk && <span className="text-red-700 font-semibold"> · overspend risk</span>}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
        {bucket('Material', cost.material_actual, cost.budgeted_material_cost)}
        {bucket('Site installation', cost.site_actual, cost.budgeted_site_cost)}
        {bucket('Labour (daily entries)', cost.labour_actual, cost.budgeted_labour_cost)}
      </div>
      <div className="text-xs mt-2 flex justify-between flex-wrap gap-2">
        <span><span className="font-semibold">Total</span> {lakh(cost.total_actual)} spent{cost.budgeted_total ? ` of ${lakh(cost.budgeted_total)}` : ''}</span>
        <span className="text-gray-400">Same figures as the Expenses dashboard</span>
      </div>
    </div>
  );
};

// Lightweight Gantt: thin grey bar = current baseline, thick bar = forecast
// (coloured by status), diamond = milestone, dashed line = today.
const GanttChart = ({ schedule, steps }) => {
  if (!steps.length) return null;
  const dates = steps.flatMap((s) => [s.baseline_start, s.baseline_end, s.projected_start, s.projected_end]).filter(Boolean);
  const min = dates.reduce((a, b) => (a < b ? a : b));
  const max = dates.reduce((a, b) => (a > b ? a : b));
  const span = Math.max(daysBetween(min, max) + 1, 1);
  const pos = (d) => `${(100 * daysBetween(min, d)) / span}%`;
  const width = (a, b) => `${Math.max((100 * (daysBetween(a, b) + 1)) / span, 0.8)}%`;
  const showToday = schedule.today >= min && schedule.today <= max;
  const baselined = schedule.current_rev !== null;

  return (
    <div className="mb-4 border border-gray-200 rounded p-3 overflow-x-auto">
      <div className="min-w-[560px]">
        <div className="flex text-xs text-gray-400 mb-1">
          <div className="w-44 shrink-0" />
          <div className="flex-1 flex justify-between"><span>{formatDateDDMMYY(min)}</span><span>{formatDateDDMMYY(max)}</span></div>
        </div>
        {steps.map((s) => (
          <div key={s.id} className="flex items-center h-8">
            <div className="w-44 shrink-0 text-xs text-gray-700 truncate pr-2" title={s.name}>{s.seq}. {s.name}</div>
            <div className="flex-1 relative h-full">
              {showToday && <div className="absolute top-0 bottom-0 border-l border-dashed border-komal-blue" style={{ left: pos(schedule.today) }} />}
              {baselined && s.baseline_start && (
                <div className="absolute bottom-1 h-1.5 rounded bg-gray-300" style={{ left: pos(s.baseline_start), width: width(s.baseline_start, s.baseline_end) }} />
              )}
              {s.is_milestone ? (
                <div className={`absolute top-1.5 w-3 h-3 rotate-45 ${SCHEDULE_BAR_COLORS[s.status]}`} style={{ left: `calc(${pos(s.projected_end)} - 6px)` }}
                  title={formatDateDDMMYY(s.projected_end)} />
              ) : (
                <div className={`absolute top-1 h-3.5 rounded ${SCHEDULE_BAR_COLORS[s.status]}`}
                  style={{ left: pos(s.projected_start), width: width(s.projected_start, s.projected_end) }}
                  title={`${formatDateDDMMYY(s.projected_start)} to ${formatDateDDMMYY(s.projected_end)}`} />
              )}
            </div>
          </div>
        ))}
        {baselined && (
          <div className="flex gap-4 text-xs text-gray-500 mt-2 ml-44 flex-wrap">
            <span className="flex items-center gap-1"><span className="inline-block w-5 h-1.5 bg-gray-300 rounded" /> Baseline</span>
            <span className="flex items-center gap-1"><span className="inline-block w-5 h-3 bg-blue-500 rounded" /> Forecast, by status</span>
            <span className="flex items-center gap-1"><span className="inline-block w-0 h-3 border-l border-dashed border-komal-blue" /> Today</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Who owns one department line on this project. Blank = the department's
// current head, so it follows a head change automatically.
const ResponsibleSelect = ({ task, employees, onChange }) => (
  <select className="text-xs border border-gray-200 rounded px-1 py-0.5 max-w-[14rem]" value={task.responsible_id || ''}
    aria-label={`Responsible for ${task.department_name || 'this line'}`}
    onChange={(e) => onChange(task, e.target.value)}>
    <option value="">Department head ({task.department_head_name || 'not set'})</option>
    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
  </select>
);

const DraftTable = ({ steps, departments, canPlan, employees, onSave, onResponsible }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b">
          <th className="py-2 pr-2">Step</th>
          <th className="py-2 pr-2">Department → responsible</th>
          <th className="py-2 pr-2">Days</th>
          <th className="py-2 pr-2">Start</th>
          <th className="py-2 pr-2">End</th>
          <th className="py-2">After</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => (
          <tr key={s.id} className="border-b border-gray-100 align-top">
            <td className="py-2 pr-2">
              <span className="text-gray-400 font-mono text-xs mr-1">{s.seq}</span>{s.name}
              {s.is_milestone && <span className="ml-1 text-xs text-gray-400">(milestone)</span>}
            </td>
            <td className="py-2 pr-2">
              <DepartmentPicker step={s} departments={departments} disabled={!canPlan}
                onChange={(ids) => onSave(s.id, { department_ids: ids })} />
              {canPlan && s.tasks.filter((t) => t.department_id).map((t) => (
                <div key={t.id} className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                  <span>{t.department_name} →</span>
                  <ResponsibleSelect task={t} employees={employees} onChange={onResponsible} />
                </div>
              ))}
            </td>
            <td className="py-2 pr-2">
              {s.is_milestone ? <span className="text-gray-400">1</span> : (
                <input type="number" min="1" className="border border-gray-300 rounded px-2 py-1 text-sm w-20" defaultValue={s.duration_days} disabled={!canPlan}
                  key={`${s.id}-${s.duration_days}`}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 1 && v !== s.duration_days) onSave(s.id, { duration_days: v });
                  }} />
              )}
            </td>
            <td className="py-2 pr-2 whitespace-nowrap">
              <input type="date" className="border border-gray-300 rounded px-2 py-1 text-sm" value={s.planned_start || ''} disabled={!canPlan}
                onChange={(e) => e.target.value && onSave(s.id, { manual_start: e.target.value })} />
              {s.manual_start && canPlan && (
                <button className="block text-xs text-komal-blue hover:underline mt-1"
                  onClick={() => onSave(s.id, { clear_manual_start: true })}>Reset to after predecessors</button>
              )}
            </td>
            <td className="py-2 pr-2 whitespace-nowrap">{formatDateDDMMYY(s.planned_end)}</td>
            <td className="py-2 text-xs text-gray-500">{s.predecessor_seqs.join(', ') || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DepartmentPicker = ({ step, departments, disabled, onChange }) => {
  const ids = step.tasks.map((t) => t.department_id).filter(Boolean);
  const available = departments.filter((d) => !ids.includes(d.id));
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {step.tasks.map((t) => (
        <span key={t.id} className={`text-xs px-2 py-0.5 rounded ${t.department_id ? 'bg-gray-100' : 'bg-red-100 text-red-800'}`}>
          {t.department_name || 'Unassigned'}
          {!disabled && t.department_id && ids.length > 1 && (
            <button className="ml-1 text-gray-400 hover:text-red-600" aria-label={`Remove ${t.department_name}`}
              onClick={() => onChange(ids.filter((id) => id !== t.department_id))}>×</button>
          )}
        </span>
      ))}
      {!disabled && available.length > 0 && (
        <select className="text-xs border border-gray-200 rounded px-1 py-0.5" value=""
          onChange={(e) => e.target.value && onChange([...ids, Number(e.target.value)])}>
          <option value="">+ Add</option>
          {available.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      )}
    </div>
  );
};

// After Rev 0. Anyone who may update a line (project HOD / PM / Projects head
// / CEO for every line; a department head or responsible person for their
// own) gets an Update button that opens the same form as Schedule Updates
// inline under the step (2026-10-26).
const TrackingTable = ({ steps, canPlan, employees, onResponsible, onTaskSaved }) => {
  const [editing, setEditing] = useState(null); // task id
  return (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b">
          <th className="py-2 pr-2">Step</th>
          <th className="py-2 pr-2">Baseline</th>
          <th className="py-2 pr-2">Forecast end</th>
          <th className="py-2 pr-2">Slip</th>
          <th className="py-2">Departments</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => {
          const editTask = s.tasks.find((t) => t.id === editing);
          return (
          <React.Fragment key={s.id}>
          <tr className={`border-b border-gray-100 align-top ${s.is_overdue ? 'bg-red-50' : ''}`}>
            <td className="py-2 pr-2">
              <span className="text-gray-400 font-mono text-xs mr-1">{s.seq}</span>{s.name}
              <div className="mt-1 flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${SCHEDULE_STATUS_COLORS[s.status]}`}>{SCHEDULE_STATUS_LABELS[s.status]}</span>
                {!s.is_milestone && <span className="text-xs text-gray-500">{s.percent_complete}%</span>}
              </div>
            </td>
            <td className="py-2 pr-2 whitespace-nowrap text-xs">
              {s.is_milestone ? formatDateDDMMYY(s.baseline_end) : `${formatDateDDMMYY(s.baseline_start)} to ${formatDateDDMMYY(s.baseline_end)}`}
            </td>
            <td className="py-2 pr-2 whitespace-nowrap">{formatDateDDMMYY(s.projected_end)}</td>
            <td className={`py-2 pr-2 whitespace-nowrap ${s.slip_days > 0 ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>{formatSlip(s.slip_days)}</td>
            <td className="py-2 text-xs">
              {s.tasks.map((t) => (
                <div key={t.id} className="mb-1">
                  <span className="font-medium">{t.department_name}</span>
                  {canPlan ? (
                    <span className="ml-1"><ResponsibleSelect task={t} employees={employees} onChange={onResponsible} /></span>
                  ) : (
                    <span className="text-gray-500"> ({t.owner_name || 'no head set'})</span>
                  )}
                  : {SCHEDULE_STATUS_LABELS[t.status]}{!s.is_milestone && ` ${t.percent_complete}%`}
                  {t.remark && <span className="text-gray-500"> — {t.remark}</span>}
                  {t.can_edit && t.department_id && editing !== t.id && (
                    <button onClick={() => setEditing(t.id)}
                      className="ml-2 text-xs px-2 py-0.5 rounded border border-komal-blue text-komal-blue hover:bg-komal-blue/5">
                      Update
                    </button>
                  )}
                </div>
              ))}
            </td>
          </tr>
          {editTask && (
            <tr className="border-b border-gray-100 bg-gray-50">
              <td colSpan={5} className="p-3">
                <p className="text-xs text-gray-500 mb-2">
                  Updating {editTask.department_name} for {s.name} on behalf of the department. The save is logged under your name.
                </p>
                <ScheduleTaskForm
                  task={{ ...editTask, baseline_end: s.baseline_end, is_milestone: s.is_milestone }}
                  successLabel={`${s.name} (${editTask.department_name})`}
                  onCancel={() => setEditing(null)}
                  onSaved={() => { setEditing(null); onTaskSaved(); }} />
              </td>
            </tr>
          )}
          </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};

const REVISION_STATUS = {
  approved: ['Approved', 'bg-green-100 text-green-800'],
  pending: ['Pending', 'bg-amber-100 text-amber-800'],
  rejected: ['Rejected', 'bg-red-100 text-red-800'],
};

const DocumentLinks = ({ projectId, revision }) => {
  const showToast = useUIStore((s) => s.showToast);
  const download = (doc) =>
    downloadRevisionDocument(projectId, revision.id, doc.id)
      .then((res) => downloadBlob(res.data, doc.filename))
      .catch(() => showToast('Failed to download the document', 'error'));
  return revision.documents.map((d) => (
    <button key={d.id} onClick={() => download(d)} className="block text-xs text-komal-blue hover:underline text-left">
      📎 {d.filename}
    </button>
  ));
};

const RevisionLog = ({ baselines, projectId }) => (
  <div className="mt-5 overflow-x-auto">
    <h3 className="text-sm font-semibold text-gray-900 mb-2">Timeline revisions</h3>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b">
          <th className="py-1 pr-2">Rev</th><th className="py-1 pr-2">Given on</th><th className="py-1 pr-2">Reason</th>
          <th className="py-1 pr-2">Cause</th><th className="py-1 pr-2">Handover</th><th className="py-1">Status</th>
        </tr>
      </thead>
      <tbody>
        {baselines.map((b) => {
          const [baseLabel, color] = REVISION_STATUS[b.status] || [b.status, 'bg-gray-100'];
          const label = b.self_approved ? 'Self-approved' : baseLabel;
          const who = b.status === 'pending' ? b.approver_name : (b.approved_by_name || b.requested_by_name);
          return (
            <tr key={b.id} className="border-b border-gray-100 align-top">
              <td className="py-1 pr-2">Rev {b.rev_no}</td>
              <td className="py-1 pr-2 whitespace-nowrap">{formatDateDDMMYY(b.requested_at?.slice(0, 10))}</td>
              <td className="py-1 pr-2">
                {b.reason}
                {b.review_note && <div className="text-xs text-gray-500">Note: {b.review_note}</div>}
                <DocumentLinks projectId={projectId} revision={b} />
              </td>
              <td className="py-1 pr-2 capitalize">{b.cause ? b.cause.replace('_', ' ') : '—'}</td>
              <td className="py-1 pr-2 whitespace-nowrap">{formatDateDDMMYY(b.handover_date)}</td>
              <td className="py-1 whitespace-nowrap">
                <span className={`text-xs px-2 py-0.5 rounded ${color}`}>{label}{who ? ` · ${who}` : ''}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Approval card (layout B). The approver decides here; everyone else sees
// who it's waiting on.
const PendingRevisionCard = ({ revision, projectId, onDecided }) => {
  const showToast = useUIStore((s) => s.showToast);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const decide = async (approve) => {
    if (!approve && !note.trim()) return setError('Add a note for the PM when rejecting');
    setBusy(true);
    try {
      const res = await decideRevision(projectId, revision.id, approve, note);
      showToast(res.data.message, 'success');
      onDecided();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save the decision');
    } finally {
      setBusy(false);
    }
  };

  const shift = revision.handover_old && revision.handover_new
    ? Math.round((Date.parse(revision.handover_new) - Date.parse(revision.handover_old)) / 86400000) : 0;
  const shown = showAll ? revision.changes : revision.changes.slice(0, 4);

  return (
    <div className="border border-amber-300 bg-amber-50 rounded p-3 mb-4 text-sm">
      <div className="flex justify-between flex-wrap gap-2">
        <span className="font-semibold">
          Rev {revision.rev_no} {revision.can_decide ? 'waiting for your approval' : `waiting for approval by ${revision.approver_name}`}
        </span>
        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pending</span>
      </div>
      <div className="mt-1">
        Raised by {revision.requested_by_name} on {formatDateDDMMYY(revision.requested_at?.slice(0, 10))} · cause:{' '}
        {revision.cause?.replace('_', ' ')} · handover{' '}
        <span className={shift > 0 ? 'text-red-700 font-semibold' : ''}>{formatSlip(shift)} ({formatDateDDMMYY(revision.handover_new)})</span>
      </div>
      <div className="text-gray-600 mt-1">"{revision.reason}"</div>
      <DocumentLinks projectId={projectId} revision={revision} />
      <div className="text-xs mt-2">
        Changed steps: {shown.map((c) => `${c.step_name} ${formatSlip(c.change_days)}`).join(', ')}
        {revision.changes.length > 4 && (
          <button className="ml-1 text-komal-blue hover:underline" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'show fewer' : `+${revision.changes.length - 4} more`}
          </button>
        )}
      </div>
      {revision.can_decide && (
        <>
          <label className="block mt-2">
            <span className="block text-xs text-gray-500 mb-1">Note to PM (required to reject)</span>
            <textarea className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" value={note}
              placeholder="Optional when approving" onChange={(e) => { setNote(e.target.value); setError(''); }} />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => decide(false)} disabled={busy} className="px-3 py-1.5 rounded border border-gray-300 bg-white disabled:opacity-60">Reject</button>
            <button onClick={() => decide(true)} disabled={busy}
              className="px-3 py-1.5 rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
              Approve as Rev {revision.rev_no}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectSchedulePanel;
